import React from "react";
import { Training } from "../../types/Training";
import TrainingPicker from "./TrainingPicker";
import TrainingSelectionViewer from "./TrainingSelectionViewer";
import EntitySelectionViewer from "./EntitySelectionViewer";
import { CompanyContext } from "../../context/CompanyContext";
import { UserContext } from "../../context/UserContext";
import {
  makeStyles,
  Theme,
  createStyles,
  Container,
  Tabs,
  Tab,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@material-ui/core";
import TabPanel from "../TabPanel";
import { useLocation, useHistory } from "react-router-dom";
import EmployeePicker from "./EmployeePicker";
import Employee from "../../types/Employee";
import GroupPicker from "./GroupPicker";
import EmployeeGroup from "../../types/EmployeeGroup";
import { GroupTypes } from "../../constants/GroupTypes";
import { Api } from "../../api";
import { useSnackbar } from "notistack";
import PageHero from "../PageHero";
import DocumentPicker from "./DocumentPicker";
import Document, { DocumentType } from "../../types/Document";
import PacketPicker from "./PacketPicker";
import DocumentGroup from "../../types/DocumentGroup";
import AssignRequest from "../../types/AssignRequest";
import ButtonSpinner from "../ButtonSpinner";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import DateHelper from "../../helpers/DateHelper";

export default function AssignmentCreate() {
  const companyContext = React.useContext(CompanyContext);
  const user = React.useContext(UserContext);
  const [trainingIds, setTrainingIds] = React.useState(Array<string>());
  const [trainings, setTrainings] = React.useState(Array<Training>());
  const [docIds, setDocIds] = React.useState(Array<string>());
  const [docs, setDocs] = React.useState(Array<Document>());
  const [packetIds, setPacketIds] = React.useState(Array<string>());
  const [packets, setPackets] = React.useState(Array<DocumentGroup>());
  const [employeeIds, setEmployeeIds] = React.useState(Array<string>());
  const [employees, setEmployees] = React.useState(Array<Employee>());
  const [groupIds, setGroupIds] = React.useState(Array<string>());
  const [groups, setGroups] = React.useState(Array<EmployeeGroup>());
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const [entitiesTabIndex, setEntitiesTabIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [override, setOverride] = React.useState(false);
  const [doNotRepeat, setDoNotRepeat] = React.useState(false);
  const [dueDate, setDueDate] = React.useState(new Date());
  const location = useLocation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  React.useEffect(() => {
    if (location.state && location.state.selectedTraining) {
      let selectedTraining = location.state.selectedTraining;
      let a = Array<string>();
      for (let data of selectedTraining) {
        a.push(data.trainingId);
      }
      setTrainingIds(a);
      setTrainings(selectedTraining);
    }
    if (location.state && location.state.selectedDocuments) {
      let selectedDocuments =
        (location.state.selectedDocuments as Document[])?.filter(
          (doc) => doc.active
        ) ?? [];
      setDocIds(selectedDocuments.map((doc) => doc.documentId));
      setDocs(selectedDocuments);
    }
    if (location.state && location.state.selectedPackets) {
      let selectedPackets = location.state.selectedPackets;
      let b = Array<string>();
      for (let data of selectedPackets) {
        b.push(data.groupId);
      }
      setPacketIds(b);
      setPackets(selectedPackets);
    }
    if (location.state && location.state.selectedEmployees) {
      let selectedEmployees = location.state.selectedEmployees;
      let a = Array<string>();
      for (let data of selectedEmployees) {
        a.push(data.employeeId);
      }
      setEmployeeIds(a);
      setEmployees(selectedEmployees);
    }
    if (location.state && location.state.selectedGroups) {
      let selectedGroups = location.state.selectedGroups;
      let a = Array<string>();
      for (let data of selectedGroups) {
        a.push(data.employeeGroupId);
      }
      setGroupIds(a);
      setGroups(selectedGroups);
    }
  }, [location.state]);

  function onTrainingSelected(training: Training) {
    let trainingId = training.trainingId;
    let trainingIdsArray = Array.from(trainingIds);
    let trainingsArray = Array.from(trainings);
    let index = trainingIdsArray.indexOf(trainingId);
    if (index === -1) {
      trainingsArray.push(training);
      trainingIdsArray.push(trainingId);
    } else {
      trainingsArray.splice(index, 1);
      trainingIdsArray.splice(index, 1);
    }

    setTrainings(trainingsArray);
    setTrainingIds(trainingIdsArray);
  }

  function onDocumentSelected(doc: Document) {
    let id = doc.documentId;
    let idsArray = Array.from(docIds);
    let array = Array.from(docs);
    let index = idsArray.indexOf(id);
    if (index === -1) {
      array.push(doc);
      idsArray.push(id);
    } else {
      array.splice(index, 1);
      idsArray.splice(index, 1);
    }

    setDocs(array);
    setDocIds(idsArray);
  }

  function onPacketSelected(doc: DocumentGroup) {
    let id = doc.groupId;
    let idsArray = Array.from(packetIds);
    let array = Array.from(packets);
    let index = idsArray.indexOf(id);
    if (index === -1) {
      array.push(doc);
      idsArray.push(id);
    } else {
      array.splice(index, 1);
      idsArray.splice(index, 1);
    }

    setPackets(array);
    setPacketIds(idsArray);
  }

  function onEmployeeSelected(employee: Employee) {
    let id = employee.employeeId;
    let idsArray = Array.from(employeeIds);
    let employeesArray = Array.from(employees);
    let index = idsArray.indexOf(id);
    if (index === -1) {
      employeesArray.push(employee);
      idsArray.push(id);
    } else {
      employeesArray.splice(index, 1);
      idsArray.splice(index, 1);
    }

    setEmployees(employeesArray);
    setEmployeeIds(idsArray);
  }

  function onGroupSelected(group: EmployeeGroup) {
    let id = group.employeeGroupId;
    let idsArray = Array.from(groupIds);
    let array = Array.from(groups);
    let index = idsArray.indexOf(id);
    if (index === -1) {
      array.push(group);
      idsArray.push(id);
    } else {
      array.splice(index, 1);
      idsArray.splice(index, 1);
    }

    setGroups(array);
    setGroupIds(idsArray);
  }

  function assignItems() {
    setLoading(true);
    let req: AssignRequest = { companyId: companyContext.companyId };
    req.trainings = trainings.map((training) => {
      return {
        trainingId: training.trainingId,
        trainingVersion: training.trainingVersion,
      };
    });
    req.employeeGroups = groupIds;
    req.employees = employees.map((employee) => employee.employeeId);
    req.documents = docs.map((doc) => doc.documentId);
    req.packets = packets.map((packet) => packet.groupId);
    req.overrideDefaults = override;
    if (override) {
      req.overrideDueDate = DateHelper.formatDate(dueDate);
      req.doNotRepeat = doNotRepeat;
    }

    Api.assignment
      .assign(req)
      .then((res) => {
        enqueueSnackbar("Assignments saved successfully", {
          variant: "success",
        });
        history.goBack();
      })
      .finally(() => setLoading(false));
  }

  return (
    <>
      <PageHero
        backhandler={() => history.goBack()}
        title="Create Assignment"
        showBack={true}
      />
      <Container maxWidth="md" style={{ paddingBottom: 64 }}>
        <h2 className={classes.header}>Assign the selected items...</h2>
        <div className={classes.list}>
          <Tabs
            className="tabs-line"
            value={selectedTabIndex}
            style={{ marginBottom: 12 }}
          >
            <Tab
              label="Training"
              className="tab"
              onClick={() => setSelectedTabIndex(0)}
            />
            <Tab
              label="Handbooks &amp; Manuals"
              className="tab"
              onClick={() => setSelectedTabIndex(1)}
            />
            <Tab
              label="Forms"
              className="tab"
              onClick={() => setSelectedTabIndex(2)}
            />
            <Tab
              label="Packets"
              className="tab"
              onClick={() => setSelectedTabIndex(3)}
            />
          </Tabs>
          <div className={classes.tabPanel}>
            <TabPanel value={selectedTabIndex} index={0}>
              <TrainingPicker
                companyId={companyContext.companyId}
                selectedTrainingIds={trainingIds}
                onTrainingSelected={onTrainingSelected}
              />
            </TabPanel>
            <TabPanel value={selectedTabIndex} index={1}>
              <DocumentPicker
                companyId={companyContext.companyId}
                selectedDocumentIds={docIds}
                onDocumentSelected={onDocumentSelected}
                documentType={DocumentType.Handbook}
              />
            </TabPanel>
            <TabPanel value={selectedTabIndex} index={2}>
              <DocumentPicker
                companyId={companyContext.companyId}
                selectedDocumentIds={docIds}
                onDocumentSelected={onDocumentSelected}
                documentType={DocumentType.Form}
              />
            </TabPanel>
            <TabPanel value={selectedTabIndex} index={3}>
              <PacketPicker
                companyId={companyContext.companyId}
                selectedDocumentIds={packetIds}
                onDocumentSelected={onPacketSelected}
              />
            </TabPanel>
          </div>
        </div>

        <TrainingSelectionViewer
          training={trainings}
          documents={docs}
          packets={packets}
          onRemoveTraining={onTrainingSelected}
          onRemoveDocument={onDocumentSelected}
          onRemovePacket={onPacketSelected}
        />

        <h2 className={classes.header}>...to these entities</h2>
        <div className={classes.list}>
          <Tabs
            className="tabs-line"
            value={entitiesTabIndex}
            style={{ marginBottom: 12 }}
          >
            <Tab
              label="Employees"
              className="tab"
              onClick={() => setEntitiesTabIndex(0)}
            />
            {user.attributes.companyAdmin ? (
              <Tab
                label="Locations"
                className="tab"
                onClick={() => setEntitiesTabIndex(1)}
              />
            ) : null}

            {user.attributes.companyAdmin ? (
              <Tab
                label="Departments"
                className="tab"
                onClick={() => setEntitiesTabIndex(2)}
              />
            ) : null}

            {user.attributes.companyAdmin ? (
              <Tab
                label="Job Types"
                className="tab"
                onClick={() => setEntitiesTabIndex(3)}
              />
            ) : null}

            {user.attributes.companyAdmin ? (
              <Tab
                label="Groups"
                className="tab"
                onClick={() => setEntitiesTabIndex(4)}
              />
            ) : null}
          </Tabs>
          <div className={classes.tabPanel}>
            <TabPanel value={entitiesTabIndex} index={0}>
              <EmployeePicker
                employeesArray={employees}
                companyId={companyContext.companyId}
                selectedIds={employeeIds}
                onItemSelected={onEmployeeSelected}
              />
            </TabPanel>
            {user.attributes.companyAdmin ? (
              <>
                <TabPanel value={entitiesTabIndex} index={1}>
                  <GroupPicker
                    companyId={companyContext.companyId}
                    selectedIds={groupIds}
                    onItemSelected={onGroupSelected}
                    type={GroupTypes.Location}
                  />
                </TabPanel>
                <TabPanel value={entitiesTabIndex} index={2}>
                  <GroupPicker
                    companyId={companyContext.companyId}
                    selectedIds={groupIds}
                    onItemSelected={onGroupSelected}
                    type={GroupTypes.Department}
                  />
                </TabPanel>
                <TabPanel value={entitiesTabIndex} index={3}>
                  <GroupPicker
                    companyId={companyContext.companyId}
                    selectedIds={groupIds}
                    onItemSelected={onGroupSelected}
                    type={GroupTypes.JobType}
                  />
                </TabPanel>
                <TabPanel value={entitiesTabIndex} index={4}>
                  <GroupPicker
                    companyId={companyContext.companyId}
                    selectedIds={groupIds}
                    onItemSelected={onGroupSelected}
                    type={GroupTypes.Custom}
                  />
                </TabPanel>
              </>
            ) : null}
          </div>
        </div>
        <EntitySelectionViewer
          users={employees ?? []}
          data={groups ?? []}
          onRemove={onGroupSelected}
          onRemoveUser={onEmployeeSelected}
        />

        <FormGroup className="assignment-check">
          <FormControlLabel
            control={
              <Checkbox
                checked={override}
                onChange={() => setOverride(!override)}
                name="checkedB"
                color="primary"
              />
            }
            label="Override Defaults"
          />
        </FormGroup>
        {override ? (
          <div
            style={{
              border: "1px solid #c9c9c9",
              borderRadius: 4,
              padding: "24px 12px",
              paddingTop: 24,
            }}
          >
            <Grid container spacing={3}>
              <Grid item sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    fullWidth
                    label="Change Due Date"
                    format="MM.dd.yyyy"
                    value={dueDate}
                    inputVariant="outlined"
                    onChange={(date, value) => setDueDate(date ?? new Date())}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item sm={6}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={doNotRepeat}
                        onChange={() => setDoNotRepeat(!doNotRepeat)}
                        name="checkedB"
                        color="primary"
                      />
                    }
                    label="Do not repeat"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </div>
        ) : null}

        <div className="buttons-container">
          <Button
            variant="outlined"
            color="primary"
            onClick={() => history.goBack()}
          >
            Cancel
          </Button>
          <ButtonSpinner
            variant="contained"
            color="primary"
            loading={loading}
            disabled={
              trainings.length + docs.length + packets.length === 0 ||
              (employees.length === 0 && groups.length === 0)
            }
            onClick={assignItems}
          >
            Assign {trainings.length + docs.length + packets.length} Items
          </ButtonSpinner>
        </div>
      </Container>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabPanel: {
      padding: "12px 12px 0 12px",
    },
    header: {
      textAlign: "center",
    },
    list: {
      width: "100%",
      margin: "0 auto",
      border: "1px solid #c9c9c9",
      borderRadius: 4,
    },
    ul: {
      backgroundColor: "inherit",
      padding: 0,
    },
  })
);
